import React from "react"
import { Box, ButtonGroup, useMediaQuery } from "@mui/material"
import { OreveButton } from "components/button/OreveButton.tsx"

type ButtonDef<ID extends string = string> = {
    id: ID
    label?: string
    icon: React.ElementType
    dataCy?: string
}

interface SelectableButtonGroupProps<ID extends string = string> {
    buttons: readonly ButtonDef<ID>[]
    selected: ID
    onChange: (id: ID) => void
    orientation?: "vertical" | "horizontal"
}

export const SelectableButtonGroup = <ID extends string>({
    buttons,
    selected,
    onChange,
    orientation,
}: SelectableButtonGroupProps<ID>) => {
    const matches = useMediaQuery("(min-width:700px)")
    const selectedIndex = buttons.findIndex((b) => b.id === selected)
    const currentOrientation =
        orientation || (matches ? "horizontal" : "vertical")

    const horizontalHighlight = {
        position: "absolute",
        top: "4px",
        left: 0,
        bottom: 0,
        width: `${100 / buttons.length}%`,
        height: "calc(100% - 8px)",
        backgroundColor: "selectableButtonGroup.highlight",
        borderRadius: "0.625rem",
        transform: `translateX(${selectedIndex * 100}%)`,
        transition: "transform 0.3s ease-in-out",
        zIndex: 0,
    }

    const verticalHighlight = {
        position: "absolute",
        top: 0,
        left: "6px",
        width: "calc(100% - 12px)",
        bottom: "4px",
        height: `${100 / buttons.length}%`,
        backgroundColor: "selectableButtonGroup.highlight",
        borderRadius: "0.625rem",
        transform: `translateY(${selectedIndex * 100}%)`,
        transition: "transform 0.3s ease-in-out",
        marginTop: "4px",
        zIndex: 0,
    }

    return (
        <ButtonGroup
            variant="text"
            orientation={currentOrientation}
            sx={{
                backgroundColor: "selectableButtonGroup.background",
                borderRadius: "10px",
                position: "relative",
                overflow: "visible",
                width:
                    currentOrientation == "vertical" ? "auto" : "fit-content",
                "& .MuiButtonGroup-grouped:not(:last-of-type)": {
                    borderRight:
                        currentOrientation != "vertical" ? "none" : undefined,
                    borderBottom:
                        currentOrientation == "vertical" ? "none" : undefined,
                },
            }}
        >
            <Box
                sx={
                    currentOrientation == "horizontal"
                        ? horizontalHighlight
                        : verticalHighlight
                }
            />
            {buttons.map((btn) => (
                <OreveButton
                    data-cy={btn.dataCy}
                    key={btn.id}
                    value={btn.label}
                    icon={btn.icon}
                    onClick={() => onChange(btn.id)}
                    sx={{
                        flex: 1,
                        whiteSpace: "nowrap",
                        color:
                            selected === btn.id
                                ? "selectableButtonGroup.textSelected"
                                : "selectableButtonGroup.textUnselected",
                        backgroundColor: "transparent",
                        zIndex: 1,
                        "&:hover, &:focus": {
                            backgroundColor: "transparent !important",
                            boxShadow: "none !important",
                            border: "none !important",
                            outline: "none !important",
                        },
                    }}
                />
            ))}
        </ButtonGroup>
    )
}
